import classNames from 'classnames';
// biome-ignore lint/style/noRestrictedImports: <AI generated code>
import { useFlags } from 'launchdarkly-react-client-sdk';
import { memo, useCallback, useMemo, useState } from 'react';

import { ArrowRight } from '@air/next-icons';
import { Button } from '@air/primitive-button';
import { Switch } from '@air/primitive-switch';

import { useFeatureFlags } from '~/hooks/useFeatureFlags';
import { useTemporaryFeatureFlags } from '~/hooks/useTemporaryFeatureFlags';
import { type FlagValue } from '../types';
import { detectType, formatJSON, parseJSONSafe, stringifyJSON } from '../utils';

type FeatureFlagsTabProps = {
  flagOverrides: Record<string, FlagValue>;
  expandedFlags: Record<string, boolean>;
  jsonInputs: Record<string, string>;
  onToggleFlag: (flagName: string, newValue: FlagValue | undefined) => void;
  onResetFlags: () => void;
  setExpandedFlags: (flags: Record<string, boolean>) => void;
  setJsonInputs: (inputs: Record<string, string>) => void;
};

/**
 * Feature Flags tab for overriding and managing feature flags.
 */
export const FeatureFlagsTab = memo(
  ({
    flagOverrides,
    expandedFlags,
    jsonInputs,
    onToggleFlag,
    onResetFlags,
    setExpandedFlags,
    setJsonInputs,
  }: FeatureFlagsTabProps) => {
    const featureFlags = useFeatureFlags();
    const temporaryFlags = useTemporaryFeatureFlags();
    const allFlags = useMemo(() => ({ ...featureFlags, ...temporaryFlags }), [featureFlags, temporaryFlags]);
    const effectiveFlags = useMemo(() => ({ ...allFlags, ...flagOverrides }), [allFlags, flagOverrides]);

    // Get raw LaunchDarkly values without any overrides
    const originalLDFlags = useFlags<Record<string, FlagValue>>();

    // Track input values for controlled components
    const [inputValues, setInputValues] = useState<Record<string, string>>({});

    const handleToggleExpanded = useCallback(
      (flagName: string, section: 'original' | 'current') => {
        const key = section === 'original' ? `${flagName}-original` : flagName;
        const newExpanded = { ...expandedFlags };
        newExpanded[key] = !newExpanded[key];
        setExpandedFlags(newExpanded);
      },
      [expandedFlags, setExpandedFlags],
    );

    const handleJsonInputChange = useCallback(
      (flagName: string, value: string) => {
        setJsonInputs({ ...jsonInputs, [flagName]: value });
      },
      [setJsonInputs, jsonInputs],
    );

    const handleInputChange = useCallback((flagName: string, value: string) => {
      setInputValues((prev) => ({ ...prev, [flagName]: value }));
    }, []);

    const handleConfirmOverride = useCallback(
      (flagName: string, type: string, originalValue: FlagValue) => {
        if (type === 'number') {
          const value = inputValues[flagName] ?? String(originalValue);
          if (value === '' || value === String(originalValue)) {
            onToggleFlag(flagName, undefined);
          } else {
            onToggleFlag(flagName, Number(value));
          }
        } else if (type === 'string') {
          const value = inputValues[flagName] ?? String(originalValue);
          if (value === '' || value === String(originalValue)) {
            onToggleFlag(flagName, undefined);
          } else {
            onToggleFlag(flagName, value);
          }
        } else if (type === 'json') {
          const txt = jsonInputs[flagName] ?? stringifyJSON(originalValue);
          if (txt === '' || txt === stringifyJSON(originalValue)) {
            onToggleFlag(flagName, undefined);
            setJsonInputs({ ...jsonInputs, [flagName]: stringifyJSON(originalValue) });
          } else {
            const parsed = parseJSONSafe(txt);
            if (parsed.ok) {
              onToggleFlag(flagName, parsed.value as FlagValue);
            } else {
              // Invalid JSON, revert to original
              setJsonInputs({ ...jsonInputs, [flagName]: stringifyJSON(originalValue) });
            }
          }
        }
      },
      [jsonInputs, onToggleFlag, setJsonInputs, inputValues],
    );

    const handleResetFlag = useCallback(
      (flagName: string, type: string, originalValue: FlagValue) => {
        onToggleFlag(flagName, undefined);
        // Reset input values
        setInputValues((prev) => ({ ...prev, [flagName]: String(originalValue) }));
        // Reset the JSON input to original value
        if (type === 'json') {
          setJsonInputs({ ...jsonInputs, [flagName]: stringifyJSON(originalValue) });
        }
      },
      [onToggleFlag, setJsonInputs, jsonInputs],
    );

    return (
      <div className="h-full space-y-3 overflow-y-auto p-3 pt-1">
        <div className="flex items-center justify-end">
          <div className="flex gap-2">
            <Button type="button" size="small" appearance="ghost" onClick={onResetFlags}>
              Reset All
            </Button>
          </div>
        </div>

        <div className="flex max-h-96 flex-col rounded border">
          <div className="grid shrink-0 grid-cols-5 gap-2 border-b bg-grey-3 px-3 py-2 font-medium text-12 text-grey-12">
            <span>Flag Name</span>
            <span>Original Value</span>
            <span>Override</span>
            <span>Current Value</span>
            <span></span>
          </div>

          <div className="flex-1 divide-y overflow-y-auto">
            {Object.entries(allFlags).map(([name]) => {
              const originalValue = originalLDFlags[name] as FlagValue | undefined;
              if (originalValue === undefined) return null; // Skip undefined flags

              const isOver = Object.hasOwn(flagOverrides, name);
              const eff = (effectiveFlags as Record<string, FlagValue>)[name];
              const actuallyOverridden = isOver && JSON.stringify(eff) !== JSON.stringify(originalValue);
              const type = detectType(originalValue);

              return (
                <div key={name} className="grid grid-cols-5 items-center gap-2 px-3 py-2 text-12 text-grey-12">
                  <span className="truncate font-mono">{name}</span>

                  {/* Original Value */}
                  <div className="font-mono">
                    {type === 'json' ? (
                      <div className="space-y-1">
                        <button
                          type="button"
                          onClick={() => handleToggleExpanded(name, 'original')}
                          className="flex items-center gap-1 hover:text-blue-7"
                        >
                          {expandedFlags[`${name}-original`] ? '▼' : '▶'}{' '}
                          {type === 'json' ? '{…}' : String(originalValue)}
                        </button>
                        {expandedFlags[`${name}-original`] && (
                          <div className="max-h-32 overflow-auto rounded bg-grey-3 p-2 text-12">
                            {formatJSON(originalValue)}
                          </div>
                        )}
                      </div>
                    ) : (
                      <span
                        className={classNames(
                          'rounded px-2 py-1',
                          actuallyOverridden ? 'text-grey-9 line-through' : 'bg-grey-3',
                        )}
                      >
                        {String(originalValue)}
                      </span>
                    )}
                  </div>

                  {/* Override Controls */}
                  <div className="min-w-0">
                    {type === 'boolean' && (
                      <Switch
                        checked={actuallyOverridden}
                        onCheckedChange={(checked) => {
                          if (checked) {
                            // Toggle ON: set to opposite of original
                            onToggleFlag(name, !originalValue);
                          } else {
                            // Toggle OFF: remove override
                            onToggleFlag(name, undefined);
                          }
                        }}
                      />
                    )}

                    {type === 'number' && (
                      <div className="relative">
                        <input
                          type="number"
                          className="w-28 rounded border px-2 py-1 pr-8 font-mono text-12"
                          value={inputValues[name] ?? String(originalValue)}
                          placeholder={String(originalValue)}
                          onChange={(e) => handleInputChange(name, e.target.value)}
                        />
                        <Button
                          appearance="filled"
                          size="small"
                          color="blue"
                          className="absolute right-1 bottom-1 size-5 p-0.5"
                          title="Confirm override"
                          onClick={() => handleConfirmOverride(name, type, originalValue)}
                        >
                          <ArrowRight className="size-3" />
                        </Button>
                      </div>
                    )}

                    {type === 'string' && (
                      <div className="relative">
                        <input
                          type="text"
                          className="w-28 rounded border px-2 py-1 pr-8 font-mono text-12"
                          value={inputValues[name] ?? String(originalValue)}
                          placeholder={String(originalValue)}
                          onChange={(e) => handleInputChange(name, e.target.value)}
                        />
                        <Button
                          appearance="filled"
                          size="small"
                          color="blue"
                          className="absolute right-1 bottom-1 size-5 p-0.5"
                          title="Confirm override"
                          onClick={() => handleConfirmOverride(name, type, originalValue)}
                        >
                          <ArrowRight className="size-3" />
                        </Button>
                      </div>
                    )}

                    {type === 'json' && (
                      <div className="relative">
                        <textarea
                          className="h-24 w-full rounded border px-2 py-1 pr-8 font-mono text-12"
                          value={jsonInputs[name] ?? stringifyJSON(originalValue)}
                          onChange={(e) => handleJsonInputChange(name, e.target.value)}
                        />
                        <Button
                          appearance="filled"
                          size="small"
                          color="blue"
                          className="absolute right-1 bottom-3 size-5 p-0.5"
                          title="Confirm override"
                          onClick={() => handleConfirmOverride(name, type, originalValue)}
                        >
                          <ArrowRight className="size-3" />
                        </Button>
                      </div>
                    )}
                  </div>

                  {/* Current Value */}
                  <div className="font-mono">
                    {type === 'json' ? (
                      <div className="space-y-1">
                        <button
                          type="button"
                          onClick={() => handleToggleExpanded(name, 'current')}
                          className="flex items-center gap-1"
                        >
                          {expandedFlags[name] ? '▼' : '▶'} {type === 'json' ? '{…}' : String(eff)}
                        </button>
                        {!!expandedFlags[name] && (
                          <div className="max-h-32 overflow-auto rounded bg-grey-3 p-2 text-12">{formatJSON(eff)}</div>
                        )}
                      </div>
                    ) : (
                      <div className="flex items-center gap-2">
                        <span className={actuallyOverridden ? 'text-blue-9' : ''}>{String(eff)}</span>
                      </div>
                    )}
                  </div>

                  {/* Reset Button */}
                  <div className="flex justify-end">
                    {actuallyOverridden && (
                      <button
                        type="button"
                        className="rounded border px-2 py-1 text-12 hover:bg-grey-3"
                        onClick={() => handleResetFlag(name, type, originalValue)}
                      >
                        Reset
                      </button>
                    )}
                  </div>
                </div>
              );
            })}
            {Object.keys(allFlags).length === 0 && (
              <div className="px-3 py-8 text-center text-14 text-grey-9">No feature flags available</div>
            )}
          </div>
        </div>
      </div>
    );
  },
);

FeatureFlagsTab.displayName = 'FeatureFlagsTab';
